import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Button, Segment, DatePicker, Bubble } from 'tea-component';
import { useDeepCompareEffect } from 'ahooks';
import moment from 'moment';
import { useIntl } from 'umi';
import FullScreenCard from '@sg/components/FullScreenCard';
import type { IFullScreenCardRef } from '@sg/components/FullScreenCard';
import TableForm from '@sg/components/TableForm';
import CascadeProjectCustom from '@sg/components/CascadeProjectCustom';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import SvgIcon from '@home/components/SvgIcon';
import { getCascadeProjectDatas, getCascadeProjectValues } from '@gp/utils/project';
import {
  SGBuriedPoint,
  PortfolioOverviewTab,
  StudiosTab,
  createPagePoint,
  createBtnPoint,
  SGBuriedBtnPoint,
} from '@sg/utils/buriedPoint';
import { usePipelineCalendarDataNew } from '../../hooks/usePipelineCalendarDataNew';
import TimeLineTableCard from './components/TimeLineTableCard';
import Highlights from './components/Highlights';
import SearchSelectMultiple from '@gp/components/SelectMultiple';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import { usePipelineCalendarAutoSave } from './hooks/usePipelineCalendarAutoSave';
import { usePipelineProjectNavigation } from './hooks/usePipelineProjectNavigation';
import type { TSegmentType, IPipelineEvent, IPipelineProject } from './type';
import './index.scss';
import './fullscreen.scss';
import useScalable from '../../hooks/useScalable';
import { CALENDAR_CARD_SIZES, CALENDAR_CARD_SIZES_FULL } from './const';
import { computeHighlights } from '../../utils';
import { hasPipelineEventContent } from './utils/eventDisplay';

const SCALE_STEPS = [0.6, 0.8, 1.0, 1.2, 1.4];
const { MonthPicker } = DatePicker;

export default function PipelineCalendar(props: any) {
  const { editable, page, studioIdPa, calendarRef } = props;
  const { formatMessage } = useIntl();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const fullScreenCardRef = React.useRef<IFullScreenCardRef>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [scaleIndex, setScaleIndex] = useState(2); // 默认 1.0
  const wasFullScreenRef = React.useRef(false);
  const [selectedMonth, setSelectedMonth] = useState<any>(moment().startOf('month'));
  const [activeSegment, setActiveSegment] = useState<TSegmentType>('in_dev');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  // 宽表过滤checkbox当前选中值
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['all']);
  // 级联筛选器当前选中值
  const [cascadeValue, setCascadeValue] = useState<string[]>([]);
  const [projectsList, setProjectsList] = useState<string[]>([]);

  // Studio/Project 二级级联配置
  const cascadeInfoList = useMemo(
    () => [
      { level: 1, key: 'studio_id', nameKey: 'studio_name', title: 'Studio', isEndPanel: false },
      { level: 2, key: 'project_id', nameKey: 'game_title', title: 'Project', isEndPanel: true },
    ],
    [],
  );

  const isStudioPage = useMemo(() => {
    return page === 'studios';
  }, [page]);

  const { handleProjectCoverClick } = usePipelineProjectNavigation(
    activeSegment,
    isStudioPage,
  );


  const tabDatasCur = useMemo(() => {
    return isStudioPage ? StudiosTab : PortfolioOverviewTab;
  }, [isStudioPage]);

  const {
    projects,
    allProjects,
    monthColumns,
    loading,
    eventTypeFilters,
    notesContent,
    handleFilterChange,
    handleActiveSegmentFilterChange,
    handleDateFilterChange,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleNotesChange,
    selectedEvent,
    setSelectedEvent,
    handleConfirm,
    studioProjectList,
    setNotesContent,
  } = usePipelineCalendarDataNew(selectedMonth, studioIdPa, activeSegment, isStudioPage, setActiveSegment);

  const {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  } = usePipelineCalendarAutoSave({
    isEditing: isEditingNotes,
    page,
    projects,
    notesContent,
    applyProjects: handleProjectsReorder,
    setNotesContent,
  });

  // projectsListCur方法每次执行，都要重新赋值一次setSelectedEvent，永远取当前月份下第一个游戏的第一个事件

  const changeFilterProjects = useCallback(
    (cascadeValueTemp = cascadeValue, selectedFiltersTemp = selectedFilters) => {

      const btnPoint = createBtnPoint(tabDatasCur, 'pipelineCalendarFilterType', selectedFiltersTemp?.toString());
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }

      let projectsTemp = [...projects] as any;
      if (!(cascadeValueTemp.includes('all') || cascadeValueTemp?.length === 0)) {
        // values 为 project_id 数组（studio_id + '_' + game_title）
        // 通过 studioProjectList 映射 project_id → game_title
        const idToTitle = new Map<string, string>();
        studioProjectList.forEach((studio: any) => {
          (studio.projects || []).forEach((p: any) => {
            idToTitle.set(p.project_id, p.game_title);
          });
        });
        const gameTitles = cascadeValueTemp?.map((v) => idToTitle.get(v))?.filter(Boolean) as string[];

        const newProjectList = projects?.filter((project) => {
          return gameTitles.includes(project.project);
        });
        projectsTemp = [...newProjectList];
      }

      if (!(selectedFiltersTemp.includes('all') || selectedFiltersTemp.length === 0)) {
        // 按事件类型过滤：每个 project 只保留 events 中 type 匹配的条目
        const filtered = projectsTemp?.map((proj: IPipelineProject) => {
          const filteredEvents: Record<string, IPipelineProject['events'][string]> = {};
          Object.entries(proj.events)?.forEach(([monthKey, monthEvents]) => {
            const matched = (monthEvents || []).filter(
              (evt) => hasPipelineEventContent(evt) && selectedFiltersTemp.includes(evt.type),
            );
            // 有匹配事件则保留（含匹配事件），无匹配则保留空态占位
            if (matched.length > 0) {
              filteredEvents[monthKey] = matched;
            } else {
              // 保留空态占位事件（milestone 为空的）
              const emptyEvent = (monthEvents || []).find((e) => !hasPipelineEventContent(e));
              filteredEvents[monthKey] = emptyEvent ? [emptyEvent] : [];
            }
          });
          return { ...proj, events: filteredEvents };
        });
        // 过滤掉所有月份都没有命中事件（仅剩空态占位）的 project
        const filteredTemp = filtered?.filter((proj: IPipelineProject) => {
          return Object.values(proj.events || {}).some((monthEvents) => {
            return (monthEvents || []).some((evt) => hasPipelineEventContent(evt));
          });
        });
        projectsTemp = [...filteredTemp];
      }

      // 最后每次得到最新projectlist时，重新设置一下当前默认勾选事件
      let firstSelectedEvent = null;
      if (projectsTemp?.length) {
        const firstProj = projectsTemp[0] || {};

        const monthEvents = firstProj?.events[selectedMonthStr] || [];
        const firstEventTemp = monthEvents?.find((evt) => !!evt.milestone);
        const firstEvent = firstEventTemp || monthEvents[0];

        if (firstEvent) {
          firstSelectedEvent = {
            eventName: firstEvent.milestone,
            projectName: firstProj.project,
            date: firstEvent.date,
            notes: firstEvent.notes || '',
            studioId: String(firstEvent.id || ''),
            type: firstEvent.type || '',
          };
        }
        setSelectedEvent(firstSelectedEvent);
        // 并给setNotesContent设置为selectedEvent的notes
        // setNotesContent(firstSelectedEvent?.notes || '');
        setNotesContent(firstEvent?.note || '');
      }

      setProjectsList([...projectsTemp]);
    },
    [projects, studioProjectList, cascadeValue, selectedFilters],
  );

  const highlights = useMemo(
    () => computeHighlights(projectsList, selectedMonth, activeSegment),
    [projectsList, selectedMonth, activeSegment],
  );

  // 将 studioProjectList 转为级联树结构
  const cascadeTreeOptions = useMemo(() => {
    if (!studioProjectList || studioProjectList.length === 0) {
      return [];
    }
    // 转换 projects → child 以适配 getCascadeProjectDatas
    // studioProjectList 已由 getFormatProjectDatas 添加了 project_id
    const transformed = studioProjectList.map((studio: any) => ({
      studio_id: studio.studio_id,
      studio_name: studio.studio_name,
      child: (studio.projects || []).map((p: any) => ({
        game_title: p.game_title,
        cover: p.cover,
        project_id: p.project_id,
      })),
    }));
    return getCascadeProjectDatas(transformed, '', cascadeInfoList);
  }, [studioProjectList, cascadeInfoList]);

  const selectMultipleOptions = useMemo(() => {
    if (!studioProjectList || studioProjectList.length === 0) {
      return [];
    }
    return studioProjectList.flatMap((studio: any) =>
      (studio?.projects || []).map((project: any) => ({
        groupKey: 'separated',
        label: project?.game_title,
        text: project?.game_title,
        value: `${studio?.studio_id}_${project?.game_title}`,
      })),
    );
  }, [studioProjectList]);

  // 计算所有最后节点的 project_id，修改getCascadeProjectValues方法
  const allCascadeValues = useMemo(() => {
    return getCascadeProjectValues(cascadeTreeOptions, false);
  }, [cascadeTreeOptions]);

  useDeepCompareEffect(() => {
    setCascadeValue(allCascadeValues);
  }, [allCascadeValues]);

  useDeepCompareEffect(() => {
    changeFilterProjects();
  }, [projects]);

  const handleCascadeChange = (values: string[]) => {
    changeFilterProjects(values, selectedFilters);
    setCascadeValue(values);
  };

  // 用户筛选器选择的当前月，转换字符串格式
  const selectedMonthStr = useMemo(() => {
    return selectedMonth ? moment(selectedMonth).format('YYYY-MM') : moment().startOf('month').format('YYYY-MM');
  }, [selectedMonth]);

  const optionsSegment = useMemo(
    () => [
      {
        key: 'in_dev',
        text: formatMessage({ id: 'dbs_in_dev' }),
        value: 'in_dev',
      },
      {
        key: 'live',
        text: formatMessage({ id: 'dbs_live' }),
        value: 'live',
      },
    ],
    [formatMessage],
  );

  useEffect(() => {
    const pagePoint = createPagePoint(tabDatasCur);

    if (pagePoint) {
      SGBuriedPoint(pagePoint);
    }
  }, [activeSegment, tabDatasCur]);

  const handleActiveSegmentChange = (value: string) => {
    if (loading) return;
    const btnPoint = createBtnPoint(tabDatasCur, 'pipelineCalendarSegment', value);
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    setActiveSegment(value as TSegmentType);
    setIsEditingNotes(false);
    handleActiveSegmentFilterChange(value);
    setSelectedFilters?.(['all']);
  };

  // 每次点击事件卡片，保存project信息到setSelectedEvent
  const handleEventClick = (evt: IPipelineEvent, proj: IPipelineProject) => {

    const btnPoint = createBtnPoint(tabDatasCur, 'pipelineCalendarEventClick', proj.project);
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    setSelectedEvent({
      eventName: evt.milestone,
      projectName: proj.project,
      date: evt.date,
      notes: evt.notes || '',
      studioId: String(evt.id || ''),
      type: evt.type || '',
    });

    handleNotesChange(evt.note || '');
  };

  const handleDateChange = (date: any) => {
    setSelectedMonth(date);
    handleDateFilterChange(date);
  };

  const handleEditToggle = () => {
    if (isEditingNotes) {
      setIsEditingNotes(false);
      return;
    }
    handleEditWithAutoSave(() => {
      const btnPoint = createBtnPoint(tabDatasCur, 'pipelineCalendarEdit', 'Edit');
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
      // 进入编辑态：记录基线，供 RoutePrompt 与自动保存对比
      captureBaseline(projects || [], notesContent || '');
      setIsEditingNotes(true);
    });
  };

  const handleToggleFullScreen = () => {
    const btnPoint = createBtnPoint(tabDatasCur, 'pipelineCalendarFullScreen', 'FullScreen');
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    if (isFullScreen) {
      fullScreenCardRef.current?.exitFullScreen();
      setIsFullScreen(false);
      return;
    }
    fullScreenCardRef.current?.requestFullScreen();
    setIsFullScreen(true);
  };

  const handleSave = async () => {
    await handleConfirm();
    clearAutoSave();
    setIsEditingNotes(false);
  };

  const handleCancel = () => {
    handleProjectsReorderCancel();
    clearAutoSave();
    setIsEditingNotes(false);
  };

  useEffect(() => {
    if (isFullScreen) {
      document.body.classList.add('databrain-body-pipeline-fullscreen');
    } else {
      document.body.classList.remove('databrain-body-pipeline-fullscreen');
      // 只在从全屏状态退出时才滚动到 wrapper 元素
      if (wasFullScreenRef.current) {
        const scrollBack = () => {
          setTimeout(() => {
            if (wrapperRef.current) {
              wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 150);
        };
        // Esc 退出：浏览器 fullscreenchange 已经先于本 effect 触发，document.fullscreenElement 此时已为 null
        // X 按钮退出：exitFullScreen 里会延时再调 document.exitFullscreen()，此时浏览器仍在全屏，需等 fullscreenchange 后再滚
        // @ts-ignore - 兼容各内核前缀字段
        const stillInBrowserFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        );
        if (!stillInBrowserFullscreen) {
          scrollBack();
        } else {
          const handleExitComplete = () => {
            document.removeEventListener('fullscreenchange', handleExitComplete);
            document.removeEventListener('webkitfullscreenchange', handleExitComplete);
            scrollBack();
          };
          document.addEventListener('fullscreenchange', handleExitComplete);
          document.addEventListener('webkitfullscreenchange', handleExitComplete);
        }
      }
    }
    wasFullScreenRef.current = isFullScreen;

    return () => {
      document.body.classList.remove('databrain-body-pipeline-fullscreen');
    };
  }, [isFullScreen]);

  const dateFormList = [
    {
      key: 'date',
      render: () => (
        <DatePickerWrapper
          className="dbs-month-picker-with-tooltip"
          label={formatMessage({ id: 'dbs_calendar_month' })}
        >
          <MonthPicker
            overlayClassName="dbs-month-picker-overlay"
            value={selectedMonth}
            onChange={handleDateChange}
            placeholder="Please Select"
            disabled={isEditingNotes}
          />
        </DatePickerWrapper>
      ),
    },
    {
      key: 'Studio',
      render: () => (
        <>
          {isStudioPage ? (
            <SearchSelectMultiple
              groups={{
                separated: 'Filter',
              }}
              label={'Project'}
              overlayClassName={
                'dbg-dynamic-filter-overlay dbg-dynamic-filter__select-overlay dbs-filter-select dbs-common-box'
              }
              options={selectMultipleOptions}
              onChange={handleCascadeChange}
              value={cascadeValue}
              searchable={true}
            />
          ) : (
            <CascadeProjectCustom
              options={cascadeTreeOptions}
              value={cascadeValue}
              onChange={handleCascadeChange}
              allValues={allCascadeValues}
              btnTitle="Studio/Project"
              disabled={isEditingNotes}
              cascadeInfoList={cascadeInfoList}
            />
          )}
        </>
      ),
    },
  ];

  const sizesCur = useMemo(() => {
    return isFullScreen ? CALENDAR_CARD_SIZES_FULL : CALENDAR_CARD_SIZES;
  }, [isFullScreen]);

  /** ************* 日历全屏、缩放 ************* **/
  const {
    size: calendarCardSize,
    onSmallerSize: handleCalendarCardSmallerSize,
    onBiggerSize: handleCalendarCardBiggerSize,
    reset: resetCalendarCardSize,
  } = useScalable({
    sizes: sizesCur,
    onSizeChange: (_size) => { },
    isFullScreen,
  });
  /** ************* 日历全屏、缩放 ************* **/

  return (
    <div
      ref={(node) => {
        (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (calendarRef) {
          (calendarRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }}
      className={classnames('dbs-pipeline-calendar-wrapper', {
        'is-fullscreen': isFullScreen,
        'is-empty': !loading && projectsList.length === 0,
      })}
    >
      <div className="dbs-segment-row">
        <div className="dbs-segment-row__left">
          <TableForm formList={dateFormList} />
          <Segment
            className="dbs-gradient-segment"
            options={optionsSegment}
            value={activeSegment}
            onChange={handleActiveSegmentChange}
            disabled={isEditingNotes || loading}
          />
        </div>
        <div className="dbs-segment-row__right">
          {editable && !isEditingNotes && (
            <Button
              onClick={(e) => {
                e?.stopPropagation(); // Prevent collapse event
                handleEditToggle();
              }}
              className="dbs-gradient-btn"
              disabled={loading}
            >
              {formatMessage({ id: 'dbs_edit' })}
            </Button>
          )}
          {editable && isEditingNotes && (
            <div className="dbs-rich-text-buttons">
              <Button
                onClick={(e) => {
                  e?.stopPropagation(); // Prevent collapse event
                  handleSave();
                }}
                style={{ marginRight: '10px' }}
                className="dbs-gradient-btn"
              >
                {formatMessage({ id: 'dbs_confirm' })}
              </Button>
              <Button
                onClick={(e) => {
                  e?.stopPropagation(); // Prevent collapse event
                  handleCancel();
                }}
                style={{ height: '38px', minWidth: '80px' }}
                className="dbs-outline-btn"
              >
                {formatMessage({ id: 'dbs_cancel' })}
              </Button>
            </div>
          )}
          <Bubble key={'dbs-focus-icon'} content={'Full Screen'}>
            <div
              className={classnames('dbs-pipeline-fullscreen-btn', {
                'is-disabled': isEditingNotes,
              })}
              onClick={(e) => {
                if (!isEditingNotes) {
                  e?.stopPropagation();
                  handleToggleFullScreen();
                }
              }}
            >
              <SvgIcon type="scale" />
            </div>
          </Bubble>
        </div>
      </div>
      <FullScreenCard
        ref={fullScreenCardRef}
        title="Game Research Report"
        smallerBtnDisabled={scaleIndex === 0}
        biggerBtnDisabled={scaleIndex === SCALE_STEPS.length - 1}
        onSmaller={handleCalendarCardSmallerSize}
        onBigger={handleCalendarCardBiggerSize}
        onFullScreenChange={(isFullScreenValue: boolean) => {
          setIsFullScreen(isFullScreenValue);
          resetCalendarCardSize();
        }}
        setIsFullScreenOut={setIsFullScreen}
      >
        {({
          isFullScreen: _isFullScreen,
          fullScreenContent: _fullScreenContent,
        }: {
          isFullScreen: boolean;
          fullScreenContent: React.RefObject<HTMLDivElement | null>;
        }) => (
          <div
            className={classnames('dbs-studio-portfolio-overview-pipeline-calendar', {
              'dbs-studio-portfolio-overview-pipeline-calendar-fullscreen': isFullScreen,
              'dbs-studio-portfolio-overview-pipeline-calendar-normal': !isFullScreen,
            })}
            style={
              SCALE_STEPS[scaleIndex] !== 1
                ? {
                  transform: `scale(${SCALE_STEPS[scaleIndex]})`,
                  transformOrigin: 'top left',
                  width: `${100 / SCALE_STEPS[scaleIndex]}%`,
                }
                : undefined
            }
          >
            <TimeLineTableCard
              projects={projectsList}
              allProjects={allProjects}
              monthColumns={monthColumns}
              eventTypeFilters={eventTypeFilters}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              cascadeValue={cascadeValue}
              changeFilterProjects={changeFilterProjects}
              onFilterChange={handleFilterChange}
              segment={activeSegment}
              isEditing={isEditingNotes}
              onProjectsReorder={handleProjectsReorder}
              onEventClick={handleEventClick}
              loading={loading}
              selectedEvent={selectedEvent}
              isFullScreen={_isFullScreen}
              calendarCardSize={calendarCardSize}
              fullScreenContent={_fullScreenContent}
              isStudioPage={isStudioPage}
              onProjectCoverClick={handleProjectCoverClick}
            />
            <Highlights
              selectedMonthStr={selectedMonthStr}
              segment={activeSegment}
              isEditing={isEditingNotes}
              notesContent={notesContent}
              onNotesChange={handleNotesChange}
              selectedEvent={selectedEvent}
              data={highlights}
              loading={loading}
              allProjects={allProjects}
            />
          </div>
        )}
      </FullScreenCard>

      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={() => {
          handleAutoSaveConfirm();
          setIsEditingNotes(true);
        }}
        onClose={() => {
          handleAutoSaveCancel();
          captureBaseline(projects || [], notesContent || '');
          setIsEditingNotes(true);
        }}
      />

      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          await handleSave();
        }}
      />
    </div>
  );
}
